/**
 * SoundExpertConfigTab — Config tab content for the SoundExpert node.
 *
 * ElevenLabs-inspired layout:
 *   - Top area: mode-specific prompt textarea (fills available space)
 *   - Bottom toolbar (compact, single line):
 *     - Model pill ("ElevenLabs V3")
 *     - Mode selector (Music / Text to Speech / Sound Effect)
 *     - Config summary chip → opens SoundConfigFlyover
 *     - Retry count + Generate button (right-aligned)
 *
 * Speech mode uses RichTextBox with @-mention support.
 * Music/SFX use plain text prompts.
 */

import { memo, useCallback, useRef, useState } from 'react'
import type { Node as ReactFlowNode } from 'reactflow'
import { RichTextBox, type RichTextBoxHandle, type RichTextBoxState } from './RichTextBox'
import { SoundConfigFlyover } from './SoundConfigFlyover'
import type { SoundExpertNodeData, VoiceProfileDict } from '../../../../generated/nodeDataTypes'

// ─── Types ──────────────────────────────────────────────────────────────────

export type SoundMode = NonNullable<SoundExpertNodeData['in_mode']>

export interface SoundExpertConfigTabProps {
  mode: SoundMode
  musicPrompt: string
  sfxPrompt: string
  speechContent: string
  duration: string
  loop: string
  lyricsMode: string
  model: string
  voiceProfile?: VoiceProfileDict
  isRunning: boolean
  retryCount: number
  canvasNodes?: ReactFlowNode[]
  onModeChange: (mode: SoundMode) => void
  onMusicPromptChange: (value: string) => void
  onSfxPromptChange: (value: string) => void
  onSpeechContentChange: (state: RichTextBoxState) => void
  onDurationChange: (value: string) => void
  onLoopChange: (value: string) => void
  onLyricsModeChange: (value: string) => void
  onModelChange: (value: string) => void
  onVoiceProfileChange?: (profile: VoiceProfileDict | undefined) => void
  onRetryCountChange: (count: number) => void
  onGenerate: () => void
  onMentionClick?: (nodeId: string) => void
}

// ─── Constants ──────────────────────────────────────────────────────────────

const MODES: SoundMode[] = ['music', 'speech', 'sfx']

const MODE_LABELS: Record<SoundMode, string> = {
  music: 'Music',
  speech: 'Text to Speech',
  sfx: 'Sound Effect',
}

const MODE_ICONS: Record<SoundMode, string> = {
  music: '\u{1F3B5}',
  speech: '\u{1F399}\uFE0F',
  sfx: '\u{1F4A5}',
}

const MODE_SHORT_LABELS: Record<SoundMode, string> = {
  music: 'Music',
  speech: 'Speech',
  sfx: 'SFX',
}

const PROMPT_PLACEHOLDERS: Record<SoundMode, string> = {
  music: 'Describe your music, e.g. upbeat jazz piano with soft drums',
  speech: '',
  sfx: 'Describe a sound effect, e.g. thunder rumbling in the distance',
}

const SPEECH_TEMPLATE = `[0.5s-6.0s] Lieutenant: "[scared] Sir, they're not going away! The strait is still closed! Should we... should we send seal?"
[6.5s-7.5s] Brump: "[firm] SEND SEALS!"`

const RETRY_OPTIONS = [1, 2, 3, 4] as const

const ACCENT = {
  base: '#06b6d4',
  bg20: 'rgba(6, 182, 212, 0.2)',
  bg40: 'rgba(6, 182, 212, 0.4)',
  bg90: 'rgba(6, 182, 212, 0.9)',
}

const MODEL_DISPLAY: Record<string, string> = {
  elevenlabs_v3: 'ElevenLabs V3',
}

// ─── Config Summary Helper ──────────────────────────────────────────────────

function getConfigSummary(
  mode: SoundMode,
  duration: string,
  loop: string,
  lyricsMode: string,
): string {
  if (mode === 'sfx') {
    const durLabel = duration === 'auto' ? 'Auto' : duration
    const loopLabel = loop === 'on' ? 'On' : 'Off'
    return `${durLabel} \u00B7 ${loopLabel}`
  }
  if (mode === 'music') {
    const durLabel = duration === 'auto' ? 'Auto' : duration
    const lyricsLabel = lyricsMode.charAt(0).toUpperCase() + lyricsMode.slice(1)
    return `${durLabel} \u00B7 ${lyricsLabel}`
  }
  // speech
  return 'Voice'
}

// ─── Component ──────────────────────────────────────────────────────────────

const SoundExpertConfigTab = memo(function SoundExpertConfigTab({
  mode,
  musicPrompt,
  sfxPrompt,
  speechContent,
  duration,
  loop,
  lyricsMode,
  model,
  voiceProfile,
  isRunning,
  retryCount,
  canvasNodes,
  onModeChange,
  onMusicPromptChange,
  onSfxPromptChange,
  onSpeechContentChange,
  onDurationChange,
  onLoopChange,
  onLyricsModeChange,
  onModelChange: _onModelChange,
  onVoiceProfileChange,
  onRetryCountChange,
  onGenerate,
  onMentionClick,
}: SoundExpertConfigTabProps) {
  const richTextRef = useRef<RichTextBoxHandle>(null)
  const [speechInitialized, setSpeechInitialized] = useState(false)
  const [isFlyoverOpen, setIsFlyoverOpen] = useState(false)

  const handleModeChange = useCallback(
    (newMode: SoundMode) => {
      onModeChange(newMode)
      setIsFlyoverOpen(false)
      // Pre-fill speech template on first switch to speech
      if (newMode === 'speech' && !speechInitialized && !speechContent) {
        setSpeechInitialized(true)
        // Set via ref after mode switch renders RichTextBox
        setTimeout(() => {
          richTextRef.current?.setText(SPEECH_TEMPLATE)
        }, 0)
      }
    },
    [onModeChange, speechInitialized, speechContent],
  )

  const isPromptEmpty =
    mode === 'music'
      ? !musicPrompt.trim()
      : mode === 'sfx'
        ? !sfxPrompt.trim()
        : !speechContent.trim()

  const generateDisabled = isRunning || isPromptEmpty
  const modelDisplay = MODEL_DISPLAY[model] || model
  const configSummary = getConfigSummary(mode, duration, loop, lyricsMode)

  return (
    <div
      className="h-full flex flex-col nopan nodrag nowheel"
      onPointerDown={(e) => e.stopPropagation()}
      data-testid="sound-expert-config-tab"
    >
      {/* Prompt area — fills available space */}
      <div className="flex-1 overflow-y-auto px-3 pt-3 pb-2 min-h-0">
        {mode === 'speech' ? (
          <RichTextBox
            containerClassName="h-full bg-surface-1 border border-border-subtle rounded p-2"
            containerTestId="speech-prompt-area"
            ref={richTextRef}
            canvasNodes={canvasNodes}
            placeholder="Write dialogue with @character mentions..."
            initialContent={speechContent || undefined}
            onContentChange={onSpeechContentChange}
            onMentionClick={onMentionClick}
            minHeight={60}
            maxHeight={120}
          />
        ) : (
          <textarea
            value={mode === 'music' ? musicPrompt : sfxPrompt}
            onChange={(e) =>
              mode === 'music'
                ? onMusicPromptChange(e.target.value)
                : onSfxPromptChange(e.target.value)
            }
            placeholder={PROMPT_PLACEHOLDERS[mode]}
            data-testid={`${mode}-prompt-input`}
            className="w-full h-full min-h-[60px] text-sm bg-surface-1 text-text-primary border border-border-subtle rounded px-2 py-1.5 resize-none focus:outline-none focus:border-cyan-500/50 transition-colors placeholder:text-text-muted/40"
          />
        )}
      </div>

      {/* Bottom toolbar — compact single line */}
      <div className="relative flex-shrink-0 border-t border-border-subtle bg-surface-0 px-3 py-2">
        {/* Flyover popover */}
        {isFlyoverOpen && (
          <SoundConfigFlyover
            mode={mode}
            duration={duration}
            loop={loop}
            lyricsMode={lyricsMode}
            voiceProfile={voiceProfile}
            onDurationChange={onDurationChange}
            onLoopChange={onLoopChange}
            onLyricsModeChange={onLyricsModeChange}
            onVoiceProfileChange={onVoiceProfileChange}
            onClose={() => setIsFlyoverOpen(false)}
          />
        )}

        <div className="flex items-center gap-1.5" data-testid="sound-config-toolbar">
          {/* Model pill */}
          <span
            className="text-xxs px-2 py-1 rounded-full bg-surface-1 border border-border-subtle text-text-muted truncate"
            data-testid="model-pill"
          >
            {modelDisplay}
          </span>

          {/* Mode selector pills */}
          <div
            className="flex items-center border border-border-subtle rounded-full overflow-hidden"
            data-testid="toolbar-mode-selector"
          >
            {MODES.map((m) => (
              <button
                key={m}
                onClick={() => handleModeChange(m)}
                data-testid={`toolbar-mode-btn-${m}`}
                className={`px-2 py-1 text-xxs font-medium transition-colors flex items-center gap-0.5 ${
                  mode === m ? 'text-foreground' : 'text-text-muted hover:text-foreground'
                }`}
                style={mode === m ? { background: ACCENT.bg20, color: ACCENT.base } : undefined}
              >
                <span className="text-xxs">{MODE_ICONS[m]}</span>
                {MODE_SHORT_LABELS[m]}
              </button>
            ))}
          </div>

          {/* Config summary chip → opens flyover */}
          <button
            onClick={() => setIsFlyoverOpen(!isFlyoverOpen)}
            className="text-xxs px-2 py-1 rounded-full bg-surface-1 border border-border-subtle text-text-muted hover:text-foreground hover:bg-surface-2 transition-colors truncate"
            data-testid="config-summary-chip"
            title={`${MODE_LABELS[mode]} settings`}
          >
            {configSummary}
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Retry count selector */}
          <select
            data-testid="retry-count-selector"
            value={retryCount}
            onChange={(e) => onRetryCountChange(Number(e.target.value))}
            disabled={isRunning}
            className="h-7 px-1.5 text-xxs bg-surface-2 text-foreground border border-border rounded focus:outline-none focus:border-white/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed appearance-none cursor-pointer"
            title="Number of versions to generate"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.5)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 4px center',
              paddingRight: '18px',
            }}
          >
            {RETRY_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}x
              </option>
            ))}
          </select>

          {/* Generate button */}
          <button
            onClick={onGenerate}
            disabled={generateDisabled}
            data-testid="generate-btn"
            className="px-3 py-1.5 rounded text-xs font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: generateDisabled ? ACCENT.bg20 : ACCENT.bg90,
              color: generateDisabled ? ACCENT.base : '#fff',
            }}
          >
            {isRunning ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  )
})

SoundExpertConfigTab.displayName = 'SoundExpertConfigTab'

export { SoundExpertConfigTab, SPEECH_TEMPLATE, ACCENT as SOUND_ACCENT }
